/**
 * GraphEvaluator — traverses the blueprint node graph and produces
 * a map of { clipName → weight } to apply to the Three.js mixer.
 *
 * Called every frame from AvatarModel.useFrame() using graphState.
 */

import { smRuntime, triggerTransition, pickClipForState } from './smRuntime'
import { runTransitionActions } from './smTransitionActions'
import { evaluateRule } from './RuleEvaluator'
import { debugState } from './debugState'
import { variableValues, getVariable } from './animVariables'
import { applyGesture, isActive as isGestureActive } from './GestureChannel'
import { getPluginNode as pluginRegistryGet } from './pluginNodeRegistry'
import { devLog } from './devLog'
import { headFollowRuntime } from './headFollowRuntime'
import { eyeFollowRuntime } from './eyeFollowRuntime.js'
import { AdditiveAnimationBlendMode } from 'three'
import { setConfig as setBlinkConfig } from './blinkRuntime'
import { layeredBoneBlendRuntime } from './layeredBoneBlendRuntime'
import { getBodyClipLoader } from './bodyClipLoaderRef'
import { rigControlState } from './rigControlState'
import { STREAM_FIRST_FRAME_CLIP, STREAM_LAST_FRAME_CLIP } from './sentenceFrameClips.js'

const _missingClipWarned = new Set()

// Change-tracked per-SM clip log — only fires a console.log when the active
// clip (or active state) changes, never every frame.
// Key: `${smNodeId}:${stateId}`  Value: last-reported clip string
const _smLastClip = new Map()

// Per-frame value cache — cleared each evaluateGraph call, prevents duplicate eval within one frame
const _valueCache = new Map()

// Per-frame plugin-tick guard: nodeId -> the weight tick() ran with this frame.
// Guarantees a plugin node's tick() + sentinel emission happen exactly once per
// frame even though it can be reached via the sideband, graph traversal, AND pin
// reads. Cleared each evaluateGraph call alongside _valueCache. (See pluginTickOnce.)
const _pluginTicked = new Map()

// Sticky random values — persist across frames so Select doesn't thrash every tick.
// Each entry: { value, min, max, randomType } — re-rolls if config changes.
// Cleared per-node on SM state entry so each state entry picks fresh.
const _stickyValues = new Map()

// BlendByBool crossfade state — persists across frames to drive the interpolation.
// Each entry: { weight, target, startWeight, startTime, endTime }
//   weight     — current interpolated blend weight (0 = A, 1 = B)
//   target     — 0 or 1, the value we're blending toward
//   startWeight — weight at the moment the transition started
//   startTime  — performance.now() when the transition started
//   endTime    — performance.now() when the transition should be complete
const _blendByBoolState = new Map()

// PrefabSlot blend state — same shape as _blendByBoolState plus lastClipName so
// the blend-out phase can still reference the clip after __PREFAB_ACTIVE__ clears.
const _prefabSlotState = new Map()

// ENG-435: per-clip "was this clip driven (weight > 0) on the previous frame".
// A non-looping clip that finished sits paused at time === duration with
// clampWhenFinished holding its final pose — and a clip that is being RE-driven
// after a gap (a new node, a re-entered state) sits at the *same* time === duration,
// because nothing ever rewinds it. Time alone can't tell the two apart, so we track
// the previous frame's driven set: a clip driven this frame but NOT last frame is a
// fresh (re)start and must reset() to frame 0 before play(); a continuously-driven
// finished clip is left clamped ("Loop=off → play once, hold", like Unreal bLoop=false).
// Two Sets swapped each frame so the hot path allocates nothing.
let _drivenPrevFrame = new Set()
let _drivenThisFrame = new Set()

export function clearStickyValues(nodeIds) {
  for (const id of nodeIds) _stickyValues.delete(id)
}

/**
 * Delete sticky values for every 'random' node in `nodes`, without
 * allocating an intermediate filter/map array. Called per-frame from
 * AvatarModel when a non-looping clip ends, so keep it tight.
 */
export function clearStickyRandomsInNodes(nodes) {
  if (!nodes) return
  for (const n of nodes) {
    if (n.type === 'random') _stickyValues.delete(n.id)
  }
}

function _rollRandom(min, max, randomType) {
  return randomType === 'int'
    ? Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min) + 1)) + Math.ceil(min)
    : Math.random() * (max - min) + min
}

/**
 * Build an evaluation context with O(1) lookups. Called once per evaluateGraph
 * and once per state's inner graph (state machines have nested graphs that need
 * their own index). The result/loopMap/additiveMap accumulators are shared
 * across parent + child contexts so inner-state evaluation contributes to the
 * same output.
 */
function buildCtx(nodes, edges, result, loopMap, additiveMap, speedMap) {
  const nodeById = new Map()
  for (const n of nodes) nodeById.set(n.id, n)
  const edgesByTarget = new Map()
  for (const e of edges) {
    let bucket = edgesByTarget.get(e.target)
    if (!bucket) { bucket = []; edgesByTarget.set(e.target, bucket) }
    bucket.push(e)
  }
  return { nodeById, edgesByTarget, result, loopMap, additiveMap, speedMap }
}

function findEdgeByHandle(ctx, targetId, handle) {
  const bucket = ctx.edgesByTarget.get(targetId)
  if (!bucket) return undefined
  for (const e of bucket) if (e.targetHandle === handle) return e
  return undefined
}

/**
 * Evaluate a value-producing node (e.g. Random), returning a number.
 * Random nodes are sticky: once rolled they hold their value across frames.
 * Re-rolls when clearStickyValues() is called OR when min/max/type changes.
 */
function evaluateValue(nodeId, ctx) {
  if (_valueCache.has(nodeId)) return _valueCache.get(nodeId)
  const node = ctx.nodeById.get(nodeId)
  let value = 0
  // Plugin scalar source: pull the node's primary scalar output via readPin. The
  // producer is ticked once (guarded) so its value is fresh before we read it.
  // evaluateValue is keyed by node (not pin), so this resolves the FIRST dataOut
  // pin — a value source has one logical scalar output, like Random/getVariable.
  if (node?.type?.startsWith('plugin.')) {
    const entry = pluginRegistryGet(node.type)
    pluginTickOnce(node, entry, ctx)
    const outPins = entry?.nodeManifest?.pins?.dataOut
    const pinId = (outPins && outPins.length) ? outPins[0].id : 'out'
    const raw = entry?.instance?.readPin?.(pinId, node.data, node.id)
    value = (typeof raw === 'boolean') ? (raw ? 1 : 0)
          : (typeof raw === 'number' && Number.isFinite(raw)) ? raw : 0
    _valueCache.set(nodeId, value)
    return value
  }
  if (node?.type === 'getVariable') {
    value = Number(getVariable(node.data.variableName)) || 0
    _valueCache.set(nodeId, value)
    return value
  }
  if (node?.type === 'prefabSlot') {
    // If evaluateNode already ran this slot, use the cached is-active value.
    // Otherwise compute it directly (e.g. when only the alpha pin is wired).
    if (_valueCache.has(nodeId)) return _valueCache.get(nodeId)
    const state = typeof window !== 'undefined' ? window.__PREFAB_ACTIVE__ : null
    value = (state?.clipName && Date.now() < state.expiresAt) ? 1 : 0
    _valueCache.set(nodeId, value)
    return value
  }
  if (node?.type === 'random') {
    const min        = node.data.min        ?? 0
    const max        = node.data.max        ?? 1
    const randomType = node.data.randomType ?? 'float'
    const sticky     = _stickyValues.get(nodeId)
    if (sticky && sticky.min === min && sticky.max === max && sticky.randomType === randomType) {
      value = sticky.value
    } else {
      value = _rollRandom(min, max, randomType)
      _stickyValues.set(nodeId, { value, min, max, randomType })
    }
  }
  _valueCache.set(nodeId, value)
  return value
}

/**
 * Tick a plugin node at most once per frame, returning the weight tick() ran
 * with. The _pluginTicked guard is set BEFORE invoking tick() so a re-entrant
 * pin read during tick() can't recurse into the same node. masterWeight === null
 * means "use the node's own weight" (sideband / pin-read paths); a number scales
 * it (the traversal path). Errors are swallowed — one bad plugin never stalls the
 * 60Hz loop.
 */
function pluginTickOnce(node, entry, ctx, masterWeight = null) {
  const prev = _pluginTicked.get(node.id)
  if (prev !== undefined) return prev
  const enabled = node.data?.enabled ?? true
  const base = masterWeight != null ? masterWeight : 1.0
  const w = enabled ? base * (node.data?.weight ?? 1.0) : 0
  _pluginTicked.set(node.id, w)
  if (entry?.instance?.tick) {
    try { entry.instance.tick(w, node.data, node.id, pinReaderFor(ctx)) }
    catch (e) { console.error(`[GraphEvaluator] plugin tick failed for ${node.type}:`, e) }
  }
  return w
}

/**
 * Per-ctx, per-frame typed-pin reader handed to plugin tick() as its 4th arg.
 * Lets a plugin pull values wired into its OWN input pins, reusing the existing
 * edge index + evaluateValue (allocation-free, memoized). Built once per graph
 * level per frame (memoized on ctx), not per node.
 */
function pinReaderFor(ctx) {
  if (ctx._pinReader) return ctx._pinReader
  const reader = Object.freeze({
    // Is nodeId's input `pinId` wired to an upstream output?
    isWired(nodeId, pinId) {
      return findEdgeByHandle(ctx, nodeId, pinId) !== undefined
    },
    // Read a scalar (number, or boolean→0|1) for nodeId's input `pinId`: the wired
    // upstream value when connected, else the node's own inline value (data[pinId],
    // set by the node's number/checkbox field), else 0.
    readScalarIn(nodeId, pinId) {
      const edge = findEdgeByHandle(ctx, nodeId, pinId)
      if (edge) return evaluateValue(edge.source, ctx)
      const inline = ctx.nodeById.get(nodeId)?.data?.[pinId]
      if (typeof inline === 'number') return Number.isFinite(inline) ? inline : 0
      if (typeof inline === 'boolean') return inline ? 1 : 0
      return 0
    },
    // Read the buffer/anim value of the producer wired into nodeId's input
    // `pinId`: resolve the edge, ensure the producer ticked, return its readPin
    // view (a retained typed array — do NOT mutate or retain). null when unwired.
    readBufferIn(nodeId, pinId) {
      const edge = findEdgeByHandle(ctx, nodeId, pinId)
      if (!edge) return null
      const src = ctx.nodeById.get(edge.source)
      if (!src) return null
      const e = pluginRegistryGet(src.type)
      pluginTickOnce(src, e, ctx)
      return e?.instance?.readPin?.(edge.sourceHandle, src.data, src.id) ?? null
    },
  })
  ctx._pinReader = reader
  return reader
}

/**
 * Recursively evaluate a node, returning the weighted contribution
 * of each animation clip.
 *
 * @param {string} nodeId
 * @param {{nodeById: Map, edgesByTarget: Map, result: Map, loopMap: Map, additiveMap: Map}} ctx
 * @param {number}  masterWeight — weight passed down from parent
 */
function evaluateNode(nodeId, ctx, masterWeight, isAdditive = false) {
  const node = ctx.nodeById.get(nodeId)
  if (!node) return

  if (node.type === 'playClip') {
    const clipName = node.data.clipName
    if (clipName) {
      ctx.result.set(clipName, (ctx.result.get(clipName) ?? 0) + masterWeight)
      // Per-node loop (ENG-524). Default FALSE — a clip plays once and holds
      // its final frame unless Loop is explicitly enabled. Existing blueprint
      // nodes (incl. the shipped idles/breathing/listening) carry an explicit
      // `loop: true`, so they keep looping; only nodes without the field (or
      // set to false) are one-shot. applyWeights turns false into LoopOnce +
      // clampWhenFinished (hold final frame), exactly like Unreal's non-loop clamp.
      if (!ctx.loopMap.has(clipName)) ctx.loopMap.set(clipName, node.data.loop === true)
      if (!ctx.additiveMap.has(clipName)) ctx.additiveMap.set(clipName, isAdditive)
      if (ctx.speedMap && !ctx.speedMap.has(clipName)) {
        const raw = Number(node.data.playSpeed)
        ctx.speedMap.set(clipName, Number.isFinite(raw) && raw > 0 ? raw : 1.0)
      }
    }
    return
  }

  // Prefab slot — activates when window.__PREFAB_ACTIVE__ is set.
  //
  // Two modes, chosen by whether a `pose` input edge is wired:
  //
  // Merged mode (pose wired): self-contained crossfade between the incoming pose
  //   and the prefab clip — no external BlendByBool needed.
  //
  // Legacy mode (pose not wired): outputs the prefab clip weight unchanged so
  //   an external BlendByBool can drive the crossfade (old wiring still works).
  //
  // Either way, is-active (0/1) is cached in _valueCache for downstream pins.
  if (node.type === 'prefabSlot') {
    const prefabGlobal = typeof window !== 'undefined' ? window.__PREFAB_ACTIVE__ : null
    const active       = !!(prefabGlobal?.clipName && Date.now() < prefabGlobal.expiresAt)
    const target       = active ? 1 : 0

    _valueCache.set(node.id, target)

    const poseEdge = findEdgeByHandle(ctx, nodeId, 'pose')

    if (poseEdge) {
      // ── Merged mode ──────────────────────────────────────────────────────
      let bs = _prefabSlotState.get(nodeId)
      if (!bs) {
        bs = { weight: target, target, startWeight: target, startTime: 0, endTime: 0, lastClipName: null }
        _prefabSlotState.set(nodeId, bs)
      }

      if (active) bs.lastClipName = prefabGlobal.clipName

      if (target !== bs.target) {
        const blendMs = (target === 1
          ? (node.data.blendInTime  ?? 0.1)
          : (node.data.blendOutTime ?? 0.1)) * 1000
        const now = performance.now()
        bs.startWeight = bs.weight
        bs.startTime   = now
        bs.endTime     = now + blendMs
        bs.target      = target
      }

      const now = performance.now()
      if (now >= bs.endTime) {
        bs.weight = bs.target
      } else {
        const t = (now - bs.startTime) / (bs.endTime - bs.startTime)
        bs.weight = bs.startWeight + (bs.target - bs.startWeight) * Math.max(0, Math.min(1, t))
      }

      const w = bs.weight  // 0 = full input pose, 1 = full prefab clip

      if (w < 1) evaluateNode(poseEdge.source, ctx, masterWeight * (1 - w), isAdditive)

      const clipBase = bs.lastClipName
      if (clipBase && w > 0) {
        const isFace   = node.data?._graphType === 'face'
        const clipName = isFace ? `${clipBase}_face` : clipBase
        ctx.result.set(clipName, (ctx.result.get(clipName) ?? 0) + masterWeight * w)
        ctx.loopMap.set(clipName, false)
        ctx.additiveMap.set(clipName, isAdditive)
      }

      if (w === 0) bs.lastClipName = null

    } else {
      // ── Legacy mode — pose pin not wired, external BlendByBool drives blend ──
      if (active) {
        const isFace   = node.data?._graphType === 'face'
        const clipName = isFace ? `${prefabGlobal.clipName}_face` : prefabGlobal.clipName
        ctx.result.set(clipName, (ctx.result.get(clipName) ?? 0) + masterWeight)
        ctx.loopMap.set(clipName, false)
        ctx.additiveMap.set(clipName, isAdditive)
      }
    }

    return
  }

  if (node.type === 'liveLink') {
    // Sentinel clip name — HeadModel watches for this to drive ARKit blendshapes
    ctx.result.set('__livelink__', (ctx.result.get('__livelink__') ?? 0) + masterWeight)
    return
  }

  // First/Last Frame Pose — source nodes that emit the static face pose the
  // Stream First/Last Frame event actions published (a 1-frame clip in
  // faceClipPlayer). Ordinary clip-weight sources: an upstream Blend / Blend by
  // Bool scales them like any other clip, and both the GLB head and the splat
  // rig resolve them via getBlendedWeights when LiveLink is off. The clip is
  // absent until the event action fires, in which case getBlendedWeights simply
  // skips it (rest) — never an error.
  if (node.type === 'firstFramePose') {
    ctx.result.set(STREAM_FIRST_FRAME_CLIP, (ctx.result.get(STREAM_FIRST_FRAME_CLIP) ?? 0) + masterWeight)
    return
  }
  if (node.type === 'lastFramePose') {
    ctx.result.set(STREAM_LAST_FRAME_CLIP, (ctx.result.get(STREAM_LAST_FRAME_CLIP) ?? 0) + masterWeight)
    return
  }

  if (node.type === 'rigController') {
    // Sentinel clip name — HeadModel (GLB morphs), OGSModel (splat rig) and
    // StreamAvatarModel (stream rig) watch for this to drive the face from
    // rigControlState (the Rig Preview sliders) instead of LiveLink/clips.
    // The graph owns the routing: this is an ordinary pose source, so an
    // upstream Blend scales it like any other.
    ctx.result.set('__rigcontrol__', (ctx.result.get('__rigcontrol__') ?? 0) + masterWeight)
    // Pose vs passthrough is authored on the NODE, so the values module never
    // has to know which blueprint is loaded.
    rigControlState.passthrough = node.data?.passthrough === true
    return
  }


  if (node.type === 'headFollow') {
    // Sentinel weight — AvatarModel picks this up post-mixer to rotate the
    // neck chain toward the camera. Per-node config (max angles, smoothing)
    // is stashed on the runtime singleton; only one active headFollow per
    // face graph is meaningful (last-writer-wins).
    ctx.result.set('__headfollow__', (ctx.result.get('__headfollow__') ?? 0) + masterWeight)
    headFollowRuntime.setConfig(node.data)
    return
  }

  if (node.type === 'eyeFollow') {
    // Sentinel weight — the renderer that owns the avatar picks this up
    // post-mixer and spends it three ways: ARKit EyeLook* morphs (GLB), the
    // splat rig's own eye controls, and a `neckWeight` share added to
    // __headfollow__ so the neck turn reuses the head-follow chain rather than
    // getting a second implementation. Same last-writer-wins config rule as
    // headFollow — only one active eyeFollow per face graph is meaningful.
    // Every field is pin-wireable (`in-<field>`), like blink.
    ctx.result.set('__eyefollow__', (ctx.result.get('__eyefollow__') ?? 0) + masterWeight)
    const resolved = {}
    for (const field of ['eyeWeight', 'neckWeight', 'maxYaw', 'maxPitch', 'smoothing', 'deadbandDeg']) {
      const edge = findEdgeByHandle(ctx, nodeId, `in-${field}`)
      resolved[field] = edge ? evaluateValue(edge.source, ctx) : node.data[field]
    }
    eyeFollowRuntime.setConfig(resolved)
    return
  }

  if (node.type === 'blink') {
    // Sentinel weight — HeadModel applies procedural blinks to morph targets
    // at the weight emitted here. Config (interval, durations) is stashed on
    // the blink runtime singleton, same pattern as headFollow.
    // Each parameter can be wired from another node (Random, GetVariable, etc.)
    // via an `in-<field>` handle, falling back to the node's own data value.
    ctx.result.set('__blink__', (ctx.result.get('__blink__') ?? 0) + masterWeight)
    const resolved = {}
    for (const field of ['minInterval', 'maxInterval', 'closeMs', 'holdMs', 'openMs']) {
      const edge = findEdgeByHandle(ctx, nodeId, `in-${field}`)
      resolved[field] = edge ? evaluateValue(edge.source, ctx) : node.data[field]
    }
    setBlinkConfig(resolved)
    return
  }

  if (node.type === 'blend') {
    let alpha = node.data.alpha ?? 0.5
    // Wired value input takes priority, then variable, then slider
    const alphaEdge = findEdgeByHandle(ctx, nodeId, 'alpha-in')
    if (alphaEdge) {
      alpha = evaluateValue(alphaEdge.source, ctx)
    } else if (node.data.alphaVariable) {
      const varVal = variableValues[node.data.alphaVariable]
      if (varVal !== undefined && varVal !== null) alpha = Number(varVal) || 0
    }
    const isAdditive = node.data.blendMode === 'additive'
    const aEdge = findEdgeByHandle(ctx, nodeId, 'a')
    const bEdge = findEdgeByHandle(ctx, nodeId, 'b')

    // Normal blend: A and B share the weight budget (1-alpha, alpha).
    // Additive blend: A is the base pose at full weight; B is a layer on
    // top, scaled by alpha. This lets sentinel channels (like __livelink__
    // in A) pass through unmuted while B (e.g. __headfollow__) stacks on.
    if (isAdditive) {
      if (aEdge) evaluateNode(aEdge.source, ctx, masterWeight,         false)
      if (bEdge) evaluateNode(bEdge.source, ctx, masterWeight * alpha, true)
    } else {
      if (aEdge) evaluateNode(aEdge.source, ctx, masterWeight * (1 - alpha), false)
      if (bEdge) evaluateNode(bEdge.source, ctx, masterWeight * alpha,       false)
    }
    return
  }

  if (node.type === 'blendByBool') {
    // Resolve bool input (wired > variable > toggle)
    let val = node.data.defaultValue ? 1 : 0
    const boolEdge = findEdgeByHandle(ctx, nodeId, 'bool-in')
    if (boolEdge) {
      val = evaluateValue(boolEdge.source, ctx)
    } else if (node.data.boolVariable) {
      const v = variableValues[node.data.boolVariable]
      if (v !== undefined && v !== null) val = Number(v) || 0
    }
    const wantTrue = val > 0.5
    const target   = wantTrue ? 1 : 0

    // Retrieve or initialise per-node blend state
    let state = _blendByBoolState.get(nodeId)
    if (!state) {
      // First frame — snap to target with no blend
      state = { weight: target, target, startWeight: target, startTime: 0, endTime: 0 }
      _blendByBoolState.set(nodeId, state)
    }

    // Detect a target flip → start a new crossfade
    if (target !== state.target) {
      const blendMs = wantTrue
        ? (node.data.trueBlendTime  ?? 0.1) * 1000
        : (node.data.falseBlendTime ?? 0.1) * 1000
      const now = performance.now()
      state.startWeight = state.weight
      state.startTime   = now
      state.endTime     = now + blendMs
      state.target      = target
    }

    // Advance the interpolation
    const now = performance.now()
    if (now >= state.endTime) {
      state.weight = state.target
    } else {
      const t = (now - state.startTime) / (state.endTime - state.startTime)
      state.weight = state.startWeight + (state.target - state.startWeight) * Math.max(0, Math.min(1, t))
    }

    const w      = state.weight  // 0 = full A, 1 = full B
    const aEdge  = findEdgeByHandle(ctx, nodeId, 'a')
    const bEdge  = findEdgeByHandle(ctx, nodeId, 'b')
    if (aEdge && w < 1) evaluateNode(aEdge.source, ctx, masterWeight * (1 - w), isAdditive)
    if (bEdge && w > 0) evaluateNode(bEdge.source, ctx, masterWeight * w,       isAdditive)
    return
  }

  if (node.type === 'select') {
    // Resolve index: wired > data.index
    const idxEdge = findEdgeByHandle(ctx, nodeId, 'in-index')
    let index = node.data.index ?? 0
    if (idxEdge) index = Math.round(evaluateValue(idxEdge.source, ctx))
    const optionCount = node.data.optionCount ?? 2
    const clamped     = Math.max(0, Math.min(optionCount - 1, index))
    const optionEdge  = findEdgeByHandle(ctx, nodeId, `option-${clamped}`)
    if (optionEdge) evaluateNode(optionEdge.source, ctx, masterWeight, isAdditive)
    return
  }

  if (node.type === 'layeredBoneBlend') {
    const weightEdge = findEdgeByHandle(ctx, nodeId, 'weight-in')
    let blendWeight = node.data.blendWeight ?? 1.0
    if (weightEdge) blendWeight = evaluateValue(weightEdge.source, ctx)
    blendWeight = Math.max(0, Math.min(1, blendWeight))

    const baseEdge  = findEdgeByHandle(ctx, nodeId, 'base')
    const layerEdge = findEdgeByHandle(ctx, nodeId, 'layer')

    // Base input → normal result (full-body clips, drive pass 1)
    if (baseEdge) evaluateNode(baseEdge.source, ctx, masterWeight, isAdditive)

    // Layer input → separate result map (drives pass 2 subtree-only blend)
    if (layerEdge && ctx.lbbResult) {
      const lbbCtx = {
        nodeById:      ctx.nodeById,
        edgesByTarget: ctx.edgesByTarget,
        result:        ctx.lbbResult,
        loopMap:       ctx.lbbLoopMap,
        additiveMap:   ctx.lbbAdditiveMap,
        speedMap:      ctx.speedMap,
      }
      evaluateNode(layerEdge.source, lbbCtx, masterWeight * blendWeight, isAdditive)
    }

    layeredBoneBlendRuntime.setConfig({
      blendBone:  node.data.blendBone ?? '',
      blendWeight,
      blendClips: new Map(ctx.lbbResult),
    })
    return
  }

  if (node.type === 'stateMachine') {
    const runtime = smRuntime[nodeId]
    if (!runtime) return

    const innerNodes = node.data.innerNodes ?? []

    // Evaluate a state's inner graph (PlayClip/Blend → output) at given weight.
    // Falls back to the legacy state.data.clipName / clips[] format for
    // blueprints authored before StateOutputNode existed.
    const evalState = (stateId, weight) => {
      const stateNode = innerNodes.find(n => n.id === stateId)
      if (!stateNode) return
      const sNodes = stateNode.data?.innerNodes ?? []
      const sEdges = stateNode.data?.innerEdges ?? []
      let outputEdge
      for (const e of sEdges) { if (e.target === 'output') { outputEdge = e; break } }
      if (outputEdge) {
        // Build a sub-ctx with fresh index maps for this state's inner graph
        const subCtx = buildCtx(sNodes, sEdges, ctx.result, ctx.loopMap, ctx.additiveMap, ctx.speedMap)
        evaluateNode(outputEdge.source, subCtx, weight, isAdditive)
        // Log subgraph path once per state activation
        const sgKey = `${nodeId}:${stateId}`
        if (_smLastClip.get(sgKey) !== '(subgraph)') {
          _smLastClip.set(sgKey, '(subgraph)')
          devLog(`[SM] ${nodeId} | state "${stateId}" → (subgraph) wt=${weight.toFixed(2)}`)
        }
        return
      }
      const legacyClip = stateId === runtime.currentState && runtime.pickedClip
        ? runtime.pickedClip
        : stateId === runtime.transition?.from && runtime.transition?.fromPickedClip
          ? runtime.transition.fromPickedClip
          : pickClipForState(stateNode)
      if (legacyClip) {
        // Log once when the clip for this state changes (re-rolls, state re-entry, etc.)
        const lcKey = `${nodeId}:${stateId}`
        if (_smLastClip.get(lcKey) !== legacyClip) {
          _smLastClip.set(lcKey, legacyClip)
          devLog(`[SM] ${nodeId} | state "${stateId}" → clip "${legacyClip}" wt=${weight.toFixed(2)}`)
        }
        ctx.result.set(legacyClip, (ctx.result.get(legacyClip) ?? 0) + weight)
        if (!ctx.loopMap.has(legacyClip)) ctx.loopMap.set(legacyClip, true)
        if (!ctx.additiveMap.has(legacyClip)) ctx.additiveMap.set(legacyClip, isAdditive)
      }
    }

    // During a crossfade transition between two states
    if (runtime.transition) {
      const elapsed  = (Date.now() - runtime.transition.startTime) / 1000
      const progress = Math.min(elapsed / runtime.transition.duration, 1)

      if (progress >= 1) {
        runtime.transition = null
      } else {
        evalState(runtime.transition.from, masterWeight * (1 - progress))
        evalState(runtime.transition.to,   masterWeight * progress)
        return
      }
    }

    // Normal — evaluate current state's inner graph at full weight
    evalState(runtime.currentState, masterWeight)
    return
  }

  // Plugin nodes — fall-through dispatch for face/body nodes contributed by the
  // plugin system. tick() is fire-and-forget (plugins schedule their own async
  // work) so we never await on the hot path. pluginTickOnce guarantees exactly one
  // tick per node per frame across the sideband, traversal, and pin reads.
  if (node.type.startsWith('plugin.')) {
    const entry = pluginRegistryGet(node.type)
    // postMixer (deformer) nodes run after mixer.update via evaluatePostMixer,
    // never in the pre-mixer weight pass — skip if traversal reaches one.
    if (entry?.nodeManifest?.phase === 'postMixer') return
    const w = pluginTickOnce(node, entry, ctx, masterWeight)
    // A plugin may declare a sentinel clip emitted into the weight map so
    // downstream consumers watching the sentinel react. perFrame
    // nodes already emitted theirs in the sideband — emitting again here would
    // DOUBLE the weight (the bug this dedup fixes), so guard on !perFrame.
    const sentinel = entry?.nodeManifest?.sentinelClip
    if (sentinel && w > 0 && !entry?.nodeManifest?.perFrame) {
      ctx.result.set(sentinel, (ctx.result.get(sentinel) ?? 0) + w)
    }
    return
  }
}

/**
 * Output pool keyed by graphState.nodes identity. evaluateGraph is on the
 * 60 Hz hot path; allocating fresh `{weights, loopSettings, additiveSettings}`
 * + 3× Object.fromEntries every frame for both body and face was the
 * dominant source of GC churn after the gesture / per-clip-GLB landings.
 * The pool keeps one output object per graph and mutates its inner maps
 * in place each frame.
 */
const _outputPool = new WeakMap()
const EMPTY_OUTPUT = { weights: null, loopSettings: {}, additiveSettings: {}, speedSettings: {} }

function _getOutput(nodes) {
  let out = _outputPool.get(nodes)
  if (!out) {
    out = { weights: {}, loopSettings: {}, additiveSettings: {}, speedSettings: {} }
    _outputPool.set(nodes, out)
  }
  if (!out.speedSettings) out.speedSettings = {}
  return out
}

function _syncObjectFromMap(obj, map) {
  // Drop stale keys, then write current entries. ~28 keys typical, so the
  // for-in scan is cheaper than reallocating a fresh object every frame.
  for (const k in obj) if (!map.has(k)) delete obj[k]
  for (const [k, v] of map) obj[k] = v
}

/**
 * Evaluate the full graph starting from the FinalPoseNode.
 *
 * Returns a pooled object — DO NOT cache the inner `weights` / `loopSettings`
 * / `additiveSettings` references across frames; they're mutated in place by
 * the next call. Iterate within the same frame and you're fine.
 *
 * @param {{ nodes: object[], edges: object[] }} graphState
 * @returns {{weights: Record<string, number> | null, loopSettings: Record<string, boolean>, additiveSettings: Record<string, boolean>}}
 */
export function evaluateGraph({ nodes, edges }) {
  _valueCache.clear()
  _pluginTicked.clear()

  const result      = new Map()
  const loopMap     = new Map()
  const additiveMap = new Map()
  const speedMap    = new Map()
  const ctx = buildCtx(nodes, edges, result, loopMap, additiveMap, speedMap)
  // LBB accumulator maps — populated only when a layeredBoneBlend node is active.
  ctx.lbbResult      = new Map()
  ctx.lbbLoopMap     = new Map()
  ctx.lbbAdditiveMap = new Map()

  const finalBucket = ctx.edgesByTarget.get('final')
  const finalEdge   = finalBucket ? finalBucket[0] : undefined

  // Sideband: tick perFrame plugin nodes every frame regardless of graph
  // topology, and emit their declared sentinelClip into ctx.result so
  // downstream watchers react even when graph traversal doesn't reach the
  // node (e.g. its `anim` edge lost a tie to a sibling source at a shared
  // input pin).
  // This sideband is the SINGLE owner of perFrame tick + sentinel emission; the
  // inline branch in evaluateNode reuses the same pluginTickOnce guard and skips
  // re-emitting their sentinel, so a perFrame node ticks once and its sentinel
  // weight is counted once.
  for (const node of nodes) {
    if (!node.type?.startsWith('plugin.')) continue
    const entry = pluginRegistryGet(node.type)
    if (!entry?.nodeManifest?.perFrame || entry.nodeManifest.phase === 'postMixer' || !entry.instance?.tick) continue
    const w = pluginTickOnce(node, entry, ctx)
    const sentinel = entry.nodeManifest?.sentinelClip
    if (sentinel && w > 0) {
      ctx.result.set(sentinel, (ctx.result.get(sentinel) ?? 0) + w)
    }
  }

  if (!finalEdge) return EMPTY_OUTPUT

  evaluateNode(finalEdge.source, ctx, 1.0, false)

  const out = _getOutput(nodes)
  if (out.weights === null) out.weights = {}
  _syncObjectFromMap(out.weights,          result)
  _syncObjectFromMap(out.loopSettings,     loopMap)
  _syncObjectFromMap(out.additiveSettings, additiveMap)
  _syncObjectFromMap(out.speedSettings,    speedMap)
  return out
}

/**
 * updateSMTransitions — evaluate transition rules for all SM nodes.
 * Must be called every frame BEFORE evaluateGraph so smRuntime is up to date.
 *
 * Transition rule types (stored in edge.data.condition):
 *   'manual'  — only fires when user clicks "go" (default)
 *   'time'    — auto-fires after edge.data.conditionValue seconds in source state
 *
 * @param {{ nodes: object[], edges: object[] }} graphState
 */
export function updateSMTransitions({ nodes }) {
  for (const node of nodes) {
    if (node.type !== 'stateMachine') continue

    // Auto-init runtime (currentState = null until rules decide otherwise)
    if (!smRuntime[node.id]) {
      smRuntime[node.id] = { currentState: null, stateEnteredAt: null, transition: null }
    }
    const runtime = smRuntime[node.id]

    const innerEdges = node.data.innerEdges ?? []
    const innerNodes = node.data.innerNodes ?? []

    // Track the entry edge target — reset currentState if it changes or is removed
    const firstEntry     = innerEdges.find(e => e.source === 'entry')
    const entryTarget    = firstEntry?.target ?? null
    const entryChanged   = runtime._entryTarget !== entryTarget
    if (entryChanged) {
      runtime._entryTarget = entryTarget
      runtime.currentState  = null
      runtime.pickedClip    = null
      runtime.transition    = null
    }

    if (runtime.transition) continue  // mid-crossfade, wait

    if (!runtime.currentState) {
      // ── No active state: always enter via first Entry edge (no rule) ──
      if (firstEntry) {
        runtime.currentState   = firstEntry.target
        runtime.stateEnteredAt = Date.now()
        const entryStateNode   = innerNodes.find(n => n.id === firstEntry.target)
        runtime.pickedClip     = pickClipForState(entryStateNode)
        // Re-roll any random nodes inside this state so each entry picks fresh
        clearStickyRandomsInNodes(entryStateNode?.data?.innerNodes)
        const clipInfo = runtime.pickedClip ? `clip: "${runtime.pickedClip}"` : '(subgraph / no clip)'
        devLog(`[SM] ${node.id} | init → "${firstEntry.target}" | ${clipInfo}`)
      }
    } else {
      // Build context for anim-time rule nodes — find clip from state's inner graph
      const currentStateNode = innerNodes.find(n => n.id === runtime.currentState)
      const sEdges = currentStateNode?.data?.innerEdges ?? []
      const sNodes = currentStateNode?.data?.innerNodes ?? []
      const outEdge = sEdges.find(e => e.target === 'output')
      const playNode = outEdge ? sNodes.find(n => n.id === outEdge.source && n.type === 'playClip') : null
      const currentClipName = playNode?.data?.clipName ?? null
      const context = { currentClipName }

      // ── Check outgoing state-to-state transitions ──
      const outEdges = innerEdges.filter(
        e => e.source === runtime.currentState && e.source !== 'entry'
      )
      for (const edge of outEdges) {
        const ruleGraph = edge.data?.ruleGraph
        if (!ruleGraph?.nodes?.length) continue
        const result = evaluateRule(ruleGraph, context)
        debugState.ruleResults[`${node.id}::${edge.id}`] = result
        if (result) {
          const toStateNode = innerNodes.find(n => n.id === edge.target)
          const fromState = runtime.currentState  // capture before triggerTransition flips it
          // Re-roll random nodes in the incoming state
          clearStickyRandomsInNodes(toStateNode?.data?.innerNodes)
          triggerTransition(node.id, runtime.currentState, edge.target, 0.3, toStateNode)
          // ENG-1062: fire the transition's Actions once, now that it's taken.
          // Decoupled seam — never imports EventRuntime onto this hot path.
          runTransitionActions(edge.data?.actions, { smNodeId: node.id, from: fromState, to: edge.target })
          break
        }
      }
    }

    // Store current active state for visual feedback
    debugState.smCurrentState[node.id] = runtime.currentState
  }
}

/**
 * Apply evaluated clip weights to a Three.js actions map.
 * Zeroes all actions not in the result.
 *
 * @param {Record<string, number> | null} weights       — from evaluateGraph()
 * @param {Record<string, boolean>}       loopSettings  — { clipName: shouldLoop }
 * @param {Record<string, THREE.AnimationAction>} actions
 */

/**
 * Place the avatar group from an extracted clip's own root motion — where the
 * character is and which way it faces, for the frame the mixer has JUST applied.
 *
 * MUST run AFTER mixer.update(), and that is the whole point of it being its own
 * function. It reads `action.time`, and the mixer is what advances that: called
 * from applyWeights (which runs before the update) it placed the group using the
 * PREVIOUS frame's time while the pose on screen was a frame ahead. The gap is
 * v * delta — about 2 cm at 60 fps and a walking pace, but 6-7 cm on a 50 ms
 * hitch — and since it varies with the frame time rather than staying constant,
 * it reads as the walk jittering whenever the frame rate is uneven. Reading the
 * time after the advance also picks up the followAction delta clamp for free.
 *
 * Scalar work, no allocation. A no-op unless a direct-played clip carries a
 * rootDrive, so gestures and preset clips never enter it.
 *
 * @param {object} actions  window.__AVATAR_ACTIONS__
 */
export function applyRootDrive(actions) {
  const w = typeof window !== 'undefined' ? window : null
  const dp = w?.__DIRECT_PLAY__
  const rd = dp?.rootDrive
  if (!rd) return
  const a = actions?.[dp.clipName]
  const g = w.__AVATAR_GROUP__
  if (!a || !g) return

  const fPos = Math.min(rd.frames - 1, Math.max(0, a.time * rd.fps))
  const i0 = Math.floor(fPos)
  const i1 = Math.min(rd.frames - 1, i0 + 1)
  const ft = fPos - i0
  const dx = rd.dx[i0] + (rd.dx[i1] - rd.dx[i0]) * ft
  const dz = rd.dz[i0] + (rd.dz[i1] - rd.dz[i0]) * ft
  const dy = rd.dyaw[i0] + (rd.dyaw[i1] - rd.dyaw[i0]) * ft
  // The delta is in the clip's own frame; the group may already be turned, and
  // the clip plays INSIDE it, so the travel rotates with it. (THREE's Y
  // rotation: x' = x cos + z sin, z' = -x sin + z cos.)
  const c = Math.cos(rd.baseYaw), sn = Math.sin(rd.baseYaw)
  g.position.x = rd.baseX + dx * c + dz * sn
  g.position.z = rd.baseZ - dx * sn + dz * c
  g.rotation.y = rd.baseYaw + dy
}

// Reused each frame by the direct-play tail blend so scaling the graph's weights
// costs no allocation on the hot path.
const _fadeWeights = {}

export function applyWeights(weights, loopSettings, actions, additiveSettings, additiveActions, additiveClipNames, speedSettings) {
  // Direct-play override: EventRuntime can set window.__DIRECT_PLAY__ to take exclusive control of one clip
  const w = typeof window !== 'undefined' ? window : null
  const dp = w?.__DIRECT_PLAY__
  // Set while a direct-play clip is fading out and the graph runs alongside it:
  // the fading action owns its own weight, so the zero-out below must leave it
  // alone — the same escape the gesture channel already uses.
  let tailClip = null
  if (dp) {
    // Expiry. Default: wall clock (dp.expiresAt). With dp.followAction the hold
    // instead tracks the ACTION's progress — under a stalled/clamped frame loop
    // (e.g. the GPU busy right after kimodo generation) the clip runs slower
    // than wall time, and a wall-clock expiry would cut it mid-motion. The clip
    // is done when the LoopOnce action has reached its end; expiresAt + 15s
    // stays as a wedge guard so a never-advancing action can't hold the graph
    // hostage forever.
    let expired
    const actFA = dp.followAction ? actions[dp.clipName] : null
    const clipDurFA = actFA?.getClip?.().duration
    if (actFA && clipDurFA) {
      expired = actFA.time >= clipDurFA - 1e-4 || Date.now() > dp.expiresAt + 15000
    } else {
      expired = Date.now() > dp.expiresAt
    }
    if (expired) {
      w.__DIRECT_PLAY__ = null
      // Release hook (e.g. generated locomotion commits its root travel to the
      // avatar group). Cleared first so a hook that plays another clip can set
      // a fresh __DIRECT_PLAY__ without this frame nulling it.
      if (typeof dp.onExpire === 'function') {
        try { dp.onExpire() } catch (err) { console.warn('[applyWeights] DIRECT_PLAY onExpire threw:', err) }
      }
    } else {
      // One-shot log when a new __DIRECT_PLAY__ kicks in — confirms the render
      // loop is actually picking up the clip the Test button asked for, and
      // shows the action it resolved to.
      if (dp._loggedClipName !== dp.clipName) {
        dp._loggedClipName = dp.clipName
        const a0 = actions[dp.clipName]
        const c0 = a0?.getClip?.()
        console.log(
          `[applyWeights] DIRECT_PLAY engaged for "${dp.clipName}" — keepBase=${!!dp.keepBase}`,
          `\n  action: ${a0 ? 'found' : 'MISSING'}`,
          a0 ? `blendMode=${a0.blendMode} (${a0.blendMode === AdditiveAnimationBlendMode ? 'ADDITIVE' : 'NORMAL'}), tracks=${c0?.tracks?.length}, duration=${c0?.duration?.toFixed(3)}s` : '',
          `\n  other actions running (will be ${dp.keepBase ? 'KEPT' : 'STOPPED'}):`,
          Object.entries(actions).filter(([n, a]) => n !== dp.clipName && a.isRunning()).map(([n]) => n).join(', ') || '(none)'
        )
      }
      // keepBase: true — let the SM continue driving its base clip so that
      // near-static overlay clips (e.g. thinking_01 with only a few active
      // tracks) blend on top of the current pose instead of snapping to A-pose.
      if (!dp.keepBase) {
        // CROSSFADE, not a cut. Stopping the outgoing actions on the first
        // direct-play frame leaves the incoming clip — still at weight ~0 from
        // its own blend-in — as the only thing driving the skeleton, and THREE
        // blends a sub-weight action toward its ORIGINAL (bind) value. So the
        // pose snapped to the A-pose and ramped in from there, which reads as a
        // skeleton jump at the start of every generated clip. Fading the
        // outgoing weight down over the same window makes the two complementary
        // (they sum to 1) and the mixer normalises them into a true crossfade.
        // The keepBase branch is already documented as avoiding exactly this
        // snap; this gives the non-keepBase path the same property without
        // leaving a second FULL-weight action driving pelvis.position, which is
        // what the duplicate-action sweep below blames for a half-length walk.
        const fadeMs = dp.blendInMs | 0
        const outWeight = fadeMs > 0 && dp.startTime != null
          ? 1 - (Date.now() - dp.startTime) / fadeMs
          : 0
        for (const [clipName, action] of Object.entries(actions)) {
          if (clipName === dp.clipName || clipName === dp.baseClipName) continue
          if (!action.isRunning()) continue
          if (outWeight > 0) action.setEffectiveWeight(outWeight)
          else action.stop()
        }
        // The registry (`actions`) has ONE action per clip name, but the mixer
        // itself can hold additional normal-blend actions bound to a different
        // clip object with the SAME name (observed empirically). A duplicate
        // left running at weight 1 makes the mixer AVERAGE it with the
        // direct-played clip on every shared track — e.g. a generated walk
        // travels exactly half its root distance. Direct-play is exclusive
        // control, so sweep the mixer itself rather than just the registry.
        // TODO: the exact source of the duplicate normal-blend action is not yet
        // confirmed (it is NOT the additive clones below, which this loop skips);
        // once found, prevent the duplicate registration and drop this sweep.
        const dpAction = actions[dp.clipName]
        const mixerActions = dpAction?.getMixer?.()._actions
        if (mixerActions) {
          for (const other of mixerActions) {
            if (other === dpAction) continue
            // Leave ADDITIVE actions alone: they blend on top (not averaged
            // like normal duplicates), the additive block below already
            // weight-zeroes them, and stop()ing them here resets their phase
            // so a persistent overlay (e.g. breathing) hitches/restarts when
            // the direct-play clip ends. Only sweep normal-blend duplicates.
            if (other.blendMode === AdditiveAnimationBlendMode) continue
            const n = other.getClip().name
            if (n === dp.clipName || n === dp.baseClipName) continue
            // Same fade as above: a duplicate cut dead is as visible as a base
            // clip cut dead, and it is often the one actually driving the pose.
            if (outWeight > 0) { other.setEffectiveWeight(outWeight); continue }
            if (other.isRunning()) other.stop()
            other.setEffectiveWeight(0)
          }
        }
      }
      if (additiveActions) {
        for (const [clipName, action] of Object.entries(additiveActions)) {
          if (clipName === dp.baseClipName) continue
          action.setEffectiveWeight(0)
        }
      }
      // Additive preview reconstruction: when dp.baseClipName is set, drive that
      // clip as a normal base pose so an additive dp.clipName shows as
      // base + delta (the full gesture) instead of the raw delta on the A-pose.
      if (dp.baseClipName && dp.baseClipName !== dp.clipName) {
        const base = actions[dp.baseClipName]
        if (base) {
          base.setLoop(2201 /* THREE.LoopRepeat */, Infinity)
          base.setEffectiveWeight(1)
          if (!base.isRunning()) base.play()
        }
      }
      const a = actions[dp.clipName]
      if (!a) {
        // JIT: a direct-played clip may still be streaming in — pull it forward.
        getBodyClipLoader()?.request(dp.clipName)
      }
      // Hoisted out of the block below so the tail blend can read them: the
      // envelope decides whether the graph runs scaled this frame.
      let dpWeight = 1
      let dpBlendOut = 0
      if (a) {
        // Linear blend-in / blend-out envelope. When dp.blendInMs and
        // dp.blendOutMs are absent or 0, this collapses to a flat 1.0
        // (legacy behavior). All ops are scalar — no per-frame allocations.
        let weight = 1
        const blendInMs  = dp.blendInMs  | 0
        const blendOutMs = dp.blendOutMs | 0
        const now = Date.now()
        if (blendInMs > 0 && dp.startTime != null) {
          const inRatio = (now - dp.startTime) / blendInMs
          if (inRatio < weight) weight = inRatio < 0 ? 0 : inRatio
        }
        if (blendOutMs > 0) {
          // followAction: fade over the clip's LAST blendOutMs of clip-time, so
          // the tail blend is frame-rate independent (see expiry note above).
          const outRatio = dp.followAction && a.getClip?.().duration
            ? ((a.getClip().duration - a.time) * 1000) / blendOutMs
            : (dp.expiresAt - now) / blendOutMs
          if (outRatio < weight) weight = outRatio < 0 ? 0 : outRatio
        }
        dpWeight = weight
        dpBlendOut = blendOutMs
        a.setEffectiveWeight(weight)
        if (!a.isRunning()) a.play()
      }
      // TAIL BLEND. While the direct clip fades out, hand the graph its own
      // weights scaled by the complement and let the normal pass below run, so
      // the clip crossfades into whatever the state machine is driving rather
      // than toward the bind pose. Symmetric with the fade-IN above.
      //
      // Only safe for a clip whose root motion was EXTRACTED: one that still
      // carries travel in pelvis.position would be dragged back the whole
      // distance as its weight falls, which is what blendOutMs=0 exists to
      // prevent on that path.
      if (dp.rootDrive && dpBlendOut > 0 && dpWeight > 0 && dpWeight < 1) {
        for (const k in _fadeWeights) delete _fadeWeights[k]
        const scale = 1 - dpWeight
        if (weights) for (const k in weights) _fadeWeights[k] = weights[k] * scale
        // Deliberately NOT added to the scaled set: its weight is already set
        // above, and handing it to the normal pass would let that pass re-play
        // it and re-apply loop settings to a LoopOnce clip in its final frames.
        // It is excluded from the zero-out instead.
        weights = _fadeWeights
        tailClip = dp.clipName
        // Fall through to the normal pass rather than returning.
      } else {

      // DIRECT_PLAY stops/overrides the normal actions; once it ends, every
      // graph-driven clip should restart cleanly rather than resume a stale,
      // possibly-finished action. Forget the previous driven set (ENG-435).
      _drivenPrevFrame.clear()
      _drivenThisFrame.clear()
      debugState.tick++
      return
      }
    }
  }

  // Stop any action whose new weight is 0. A stopped action is skipped by
  // mixer.update entirely — keeping every registered clip "running at weight 0"
  // would force the mixer to interpolate every track of every clip every frame.
  // Exception: if the gesture channel is active, GestureChannel owns its
  // clip's weight for the envelope duration — don't let the zero-out loop
  // stop the gesture action out from under it.
  //
  // Pair management: non-additive-authored clips have BOTH a normal action
  // in `actions` AND a pre-baked additive clone in `additiveActions`. Exactly
  // one of the pair should be driven per frame (decided by additiveSettings).
  // If we only zero the normal map, the additive clone keeps its last weight
  // forever — visible as walk_fwd still playing after the Blend node is
  // switched from Additive→Normal or disconnected entirely.
  const gestureClip = isGestureActive() ? window.__GESTURE_CHANNEL__?.active?.clipName : null
  for (const clipName in actions) {
    if (clipName === gestureClip || clipName === tailClip) continue
    const w2 = weights?.[clipName] ?? 0
    const drivenAsAdditiveClone =
      w2 > 0 &&
      additiveSettings?.[clipName] === true &&
      additiveActions?.[clipName] &&
      !additiveClipNames?.has(clipName)
    if (w2 <= 0 || drivenAsAdditiveClone) {
      const action = actions[clipName]
      if (action.isRunning()) action.stop()
      else action.setEffectiveWeight(0)
    }
  }

  // Symmetric zero-out for additive clones: any clone NOT being driven as
  // additive this frame gets zeroed. Manifest-additive clips share their
  // single in-place-baked action with `actions` (no entry in additiveActions),
  // so they're unaffected here.
  if (additiveActions) {
    for (const clipName in additiveActions) {
      const w2 = weights?.[clipName] ?? 0
      const isAdditiveThisFrame = w2 > 0 && (additiveSettings?.[clipName] === true)
      if (!isAdditiveThisFrame) {
        additiveActions[clipName].setEffectiveWeight(0)
      }
    }
  }

  if (!weights) {
    // idle — all stopped. Nothing is driven, so the next active frame should
    // treat every clip as a fresh start (ENG-435).
    _drivenPrevFrame.clear()
    _drivenThisFrame.clear()
    debugState.tick++
    return
  }

  // Rebuild the "driven this frame" set from scratch (cleared, refilled in the
  // loop below). The previous frame's set is still in _drivenPrevFrame.
  _drivenThisFrame.clear()

  // for-in over weights instead of Object.entries — saves one fresh array of
  // [k,v] pairs every frame. action.blendMode / setLoop / clampWhenFinished
  // are only written when they'd actually change, so the steady-state cost
  // per active clip is just setEffectiveWeight + maybe play().
  for (const clipName in weights) {
    const weight = weights[clipName]
    if (weight <= 0) continue
    const wasDriven = _drivenPrevFrame.has(clipName)
    _drivenThisFrame.add(clipName)
    const action = actions[clipName]
    if (!action) {
      // JIT: the graph is asking for a clip that hasn't registered yet. Most
      // body clips stream in the background after __AVATAR_READY__ — pull this
      // one to the front of the load queue so it's available within a tick or
      // two. request() is idempotent and cheap (Map.has + return in steady
      // state); it fires at most once per clip before that clip starts loading.
      const loader = getBodyClipLoader()
      if (loader) loader.request(clipName)
      // Only warn for a clip that's genuinely unknown to the loader — one it
      // doesn't own in any state (queued / in flight / loaded). A clip we just
      // pulled is being handled and will arrive shortly, so stay quiet.
      if (w?.__AVATAR_READY__ && !_missingClipWarned.has(clipName)
          && !(loader && loader.isManaged(clipName))) {
        _missingClipWarned.add(clipName)
        console.warn(`[applyWeights] Clip not in mixer: "${clipName}" (weight=${weight.toFixed(3)})`)
      }
      continue
    }
    const shouldLoop  = loopSettings?.[clipName] !== false
    const isAdditive  = additiveSettings?.[clipName] === true || additiveClipNames?.has(clipName)
    // When additive, prefer the pre-baked clone in additiveActions. The clone is
    // built lazily (AvatarModel no longer bakes one for every clip up front —
    // that janked the load loop), so create it on first additive use here.
    let resolvedAction = action
    if (isAdditive) {
      let clone = additiveActions?.[clipName]
      if (!clone && w?.__ENSURE_ADDITIVE_CLONE__) clone = w.__ENSURE_ADDITIVE_CLONE__(clipName)
      if (clone) resolvedAction = clone
    }
    const wantBlend   = isAdditive ? 2501 /* THREE.AdditiveAnimationBlendMode */ : 2500 /* THREE.NormalAnimationBlendMode */
    const wantLoop    = shouldLoop ? 2201 /* THREE.LoopRepeat */ : 2200 /* THREE.LoopOnce */
    if (resolvedAction.blendMode !== wantBlend) resolvedAction.blendMode = wantBlend
    if (resolvedAction.loop !== wantLoop) resolvedAction.setLoop(wantLoop, shouldLoop ? Infinity : 1)
    const wantClamp   = !shouldLoop
    if (resolvedAction.clampWhenFinished !== wantClamp) resolvedAction.clampWhenFinished = wantClamp
    const wantSpeed = speedSettings?.[clipName] ?? 1
    if (resolvedAction.timeScale !== wantSpeed) resolvedAction.timeScale = wantSpeed
    resolvedAction.setEffectiveWeight(weight)
    if (!resolvedAction.isRunning()) {
      // A non-running action is one of: never played, stopped, or a finished
      // LoopOnce clip paused at its final frame (clampWhenFinished). Bare play()
      // does NOT rewind a finished LoopOnce action — it resumes paused at the end,
      // leaving the clip frozen forever (ENG-435). Rewind via reset() when the
      // clip should loop (a looping clip must never sit paused) OR when it is a
      // fresh (re)start — driven this frame but not last frame. A non-looping clip
      // driven continuously is left clamped so it holds its final pose.
      if (shouldLoop || !wasDriven) resolvedAction.reset()
      resolvedAction.play()
    }
  }

  // Layer in any active body gesture AFTER the main weight loop so the
  // envelope-driven weight isn't clobbered by the graph's per-frame reset.
  applyGesture(actions)

  // Promote this frame's driven set to "previous" for the next frame. Swap
  // references (no allocation); the now-stale set is cleared before the next
  // refill above (ENG-435).
  const _swap = _drivenPrevFrame
  _drivenPrevFrame = _drivenThisFrame
  _drivenThisFrame = _swap

  debugState.tick++
}

/**
 * Post-mixer deformer pass. Called from AvatarModel.useFrame AFTER mixer.update()
 * (and skeleton update), so plugin deformer nodes (graph body/face,
 * phase:"postMixer") see the FINAL skinned pose — which a rig-driven ONNX cloth/
 * face deformer needs, vs the pre-mixer weight pass that only produces clip weights.
 *
 * Each such node's `deform(frameCtx, data, id)` runs once per frame. `frameCtx`
 * exposes the posed rig + audio state: `{ delta, getSkeleton(), getArkit() }`.
 * Deformers drive their own meshes (GPU morph / vertex upload) and contribute no
 * clip weights, so nothing is returned. Allocation-free: no ctx/Maps built here;
 * deformers read posed state from frameCtx (wired postMixer pin inputs are a later
 * addition). One bad deformer never stalls the loop (errors swallowed).
 */
export function evaluatePostMixer({ nodes }, frameCtx) {
  for (const node of nodes) {
    if (!node.type?.startsWith('plugin.')) continue
    const entry = pluginRegistryGet(node.type)
    if (entry?.nodeManifest?.phase !== 'postMixer' || !entry.instance?.deform) continue
    if ((node.data?.enabled ?? true) === false) continue
    try { entry.instance.deform(frameCtx, node.data, node.id) }
    catch (e) { console.error(`[GraphEvaluator] plugin deform failed for ${node.type}:`, e) }
  }
}

// Test/debug hooks: evaluate a hand-built graph / run the post-mixer deform pass
// directly (used by tests/e2e/ci/plugin-system.spec.js to exercise plugin pin
// dataflow, the single-tick guarantee, and the deformer lane off the render loop).
if (typeof window !== 'undefined') {
  window.__EVALUATE_GRAPH__ = evaluateGraph
  window.__EVALUATE_POSTMIXER__ = evaluatePostMixer
}
